/**
 * V70 타자왕 — 리더보드 페이지 (Phase 2)
 *
 * 샘플 데이터 (mock). Phase 3 서버 연결 시 실제 데이터로 교체.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, ArrowLeft, Medal, Award } from 'lucide-react';
import { acidRainApi } from './acidRain/serverApi';
import { isPhase3cServerEnabled } from './shared/phase3cFlag';
import { buildLeaderboardSlots, LEADERBOARD_EMPTY_CELL } from './shared/leaderboardSlots';
import type { LeaderboardRow } from './acidRain/types';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { useOrgUnitsPublic } from '../../shared/hooks/useOrgUnitsPublic';
import { resolveLeaderboardOrgName, buildOrgMap } from './acidRain/orgLabel';
// ISSUE-01 재제보: 저장 record 의 '이름 없음' / '' 극복 — Firestore users.nameKo override
import { resolveUserNames, resolveDisplayName } from './shared/userNameResolver';
// V70-J — 게임대회 공통 헤더
import { TournamentPageHeader, tournamentPageStyle } from './shared/TournamentPageHeader';
// V70-M — 게임대회 다크 테마 (카드 #111318 · 표 행 구분선 #303540 · 빈 순위 회색)
import { DARK, DARK_EMPTY_CELL, darkSecondaryBtn } from './shared/tournamentDarkTheme';

export function AcidRainLeaderboardPage() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { orgUnits } = useOrgUnitsPublic();
  const orgMap = useMemo(() => buildOrgMap(orgUnits || []), [orgUnits]);
  const [rows, setRows] = useState<LeaderboardRow[] | null>(null);
  const [myBest, setMyBest] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  // ISSUE-01 재제보: 조회된 각 UID 의 Firestore nameKo map
  const [nameMap, setNameMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [list, me] = await Promise.all([
          acidRainApi.getLeaderboard(),
          user ? acidRainApi.getMyBestScore(user.profile.id) : Promise.resolve({ bestScore: null, rank: null, epoch: 0 }),
        ]);
        if (cancelled) return;
        setRows(list);
        // V70-C — mock 모드의 내 최고점은 브라우저(localStorage) 기록이므로 공식 리더보드 화면에 표시하지 않음
        setMyBest(isPhase3cServerEnabled() ? me.bestScore : null);
        // ISSUE-01 재제보: 리더보드 UID 별 Firestore nameKo 조회 (demo-* skip, fail-open)
        try {
          const map = await resolveUserNames(list.map((r) => r.uid));
          if (!cancelled) setNameMap(map);
        } catch { /* fail-open */ }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <div style={pageStyle}>
      {/* V70-J — e스포츠 공통 헤더 + 기존 카드 (게임명 제목은 페이지 제목 중복을 피해 h2) */}
      <div style={{ width: '100%', maxWidth: 720, minWidth: 0 }}>
      <TournamentPageHeader page="leaderboard" style={{ marginBottom: 14 }} />
      <div style={cardStyle}>
        {/* QA Issue #1: 서버 모드일 때 데모 배너 → 공식 기록 안내로 교체 */}
        {/* V70-D — mock 모드 안내 문구 제거 (1~10위 고정 표·"—" 는 유지) */}
        {isPhase3cServerEnabled() && (
          <div style={{ ...demoBannerStyle, background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.4)', color: '#86efac' }}>
            공식 리더보드 — 모든 플레이어의 서버 저장 기록
          </div>
        )}
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            <Trophy size={22} style={{ marginRight: 8, verticalAlign: 'middle', color: '#fbbf24' }} />
            엔글 타자왕 리더보드
          </h2>
          <button style={backBtnStyle} onClick={() => navigate('/tournament/games')}>
            <ArrowLeft size={14} /> 게임센터
          </button>
        </div>

        <div style={myBestBoxStyle}>
          <span style={{ color: DARK.textMuted, fontSize: 12 }}>내 최고점</span>
          <span style={{ color: '#22d3ee', fontWeight: 700, fontSize: 20, marginLeft: 12 }}>
            {myBest != null ? myBest.toLocaleString() : '기록 없음'}
          </span>
        </div>

        {loading && <div style={emptyStyle}>로딩 중…</div>}
        {/* V70-C — 게임센터와 같은 정책: 1~10위 행 항상 표시, 유효 기록이 없는 순위는 "—" */}
        {!loading && (
          <div style={tableWrapStyle}>
            <table style={tableStyle}>
              {/* V70-M — 비율 열 폭 + 말줄임: 모바일에서 이름이 세로로 꺾이지 않게 */}
              <colgroup>
                <col style={{ width: '17%' }} />
                <col style={{ width: '33%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '28%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={thStyle}>순위</th>
                  <th style={thStyle}>이름</th>
                  <th style={thStyle}>소속</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>최고 점수</th>
                </tr>
              </thead>
              <tbody>
                {buildLeaderboardSlots(rows, (r) => r.uid, (r) => r.bestScore, isPhase3cServerEnabled()).map(({ rank, row: r }) => {
                  if (!r) {
                    return (
                      <tr key={`empty-${rank}`} data-testid="lb-empty-row">
                        <td style={{ ...tdStyle, color: DARK_EMPTY_CELL }}>{rank}</td>
                        <td style={{ ...tdStyle, color: DARK_EMPTY_CELL }}>{LEADERBOARD_EMPTY_CELL}</td>
                        <td style={{ ...tdStyle, color: DARK_EMPTY_CELL }}>{LEADERBOARD_EMPTY_CELL}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: DARK_EMPTY_CELL }}>{LEADERBOARD_EMPTY_CELL}</td>
                      </tr>
                    );
                  }
                  const org = resolveLeaderboardOrgName(r, orgMap);
                  const toneMap = {
                    sample:     { bg: 'rgba(251, 191, 36, 0.15)', fg: '#fcd34d' },
                    org:        { bg: 'rgba(34, 211, 238, 0.15)', fg: '#67e8f9' },
                    unassigned: { bg: 'rgba(148, 163, 184, 0.15)', fg: '#cbd5e1' },
                    archived:   { bg: 'rgba(248, 113, 113, 0.15)', fg: '#fca5a5' },
                    unknown:    { bg: 'rgba(148, 163, 184, 0.15)', fg: '#94a3b8' },
                  } as const;
                  const t = toneMap[org.kind];
                  return (
                    <tr key={r.uid} data-testid="lb-record-row">
                      <td style={tdStyle}>
                        <RankBadge rank={rank} />
                      </td>
                      {/* ISSUE-01 재제보: Firestore nameKo 우선, 저장 '이름 없음' 하드코딩 fallback 은 제거 */}
                      <td style={{ ...tdStyle, fontWeight: 600 }} title={resolveDisplayName(r.uid, r.displayName, nameMap)}>{resolveDisplayName(r.uid, r.displayName, nameMap)}</td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: 10,
                            background: t.bg,
                            color: t.fg,
                            fontSize: 11,
                            fontWeight: 600,
                            display: 'inline-block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            verticalAlign: 'middle',
                          }}
                          title={org.kind === 'org' ? org.text : `${org.kind}: ${org.text}`}
                        >
                          {org.text}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#fbbf24', fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>
                        {r.bestScore.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#fbbf24', fontWeight: 700 }}>
        <Trophy size={16} /> 1
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#cbd5e1', fontWeight: 700 }}>
        <Medal size={16} /> 2
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#f59e0b', fontWeight: 700 }}>
        <Award size={16} /> 3
      </span>
    );
  }
  return <span style={{ color: DARK.textMuted, fontWeight: 500 }}>{rank}</span>;
}

// V70-K — 게임대회 전용 네이비 배경으로 통일 (기존 어두운 그라데이션 대체, 카드 내부 배경은 그대로)
const pageStyle: React.CSSProperties = {
  ...tournamentPageStyle({ x: 24, y: 24 }),
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 720,
  background: DARK.card,
  border: `1px solid ${DARK.border}`,
  borderRadius: 16,
  padding: 24,
  color: DARK.text,
};

const demoBannerStyle: React.CSSProperties = {
  background: 'rgba(251, 191, 36, 0.1)',
  border: '1px solid rgba(251, 191, 36, 0.3)',
  color: '#fbbf24',
  fontSize: 12,
  padding: '6px 10px',
  borderRadius: 6,
  marginBottom: 12,
  textAlign: 'center',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 12,
  flexWrap: 'wrap',
  gap: 8,
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 800,
  margin: 0,
  color: DARK.text,
};

const backBtnStyle: React.CSSProperties = {
  ...darkSecondaryBtn,
  padding: '6px 12px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 13,
};

const myBestBoxStyle: React.CSSProperties = {
  padding: '10px 14px',
  background: DARK.row,
  border: `1px solid ${DARK.border}`,
  borderRadius: 8,
  marginBottom: 14,
  display: 'flex',
  alignItems: 'center',
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: 'auto',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 6px',
  color: DARK.textMuted,
  fontSize: 12,
  fontWeight: 500,
  borderBottom: `1px solid ${DARK.border}`,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  // 머리글은 좁은 화면에서 단어 단위 줄바꿈 (잘림 방지). 데이터 칸은 한 줄 유지
  wordBreak: 'keep-all',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 6px',
  borderBottom: `1px solid ${DARK.border}`,
  fontSize: 13,
  color: DARK.text,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const emptyStyle: React.CSSProperties = {
  padding: 30,
  textAlign: 'center',
  color: DARK.textMuted,
};

export default AcidRainLeaderboardPage;
